import { useEffect, useState } from 'react';
import { text } from '../app/i18n';
import { fieldLabel, fieldOf, type ObjectSummary } from './format';
import { ClearableInput } from '../design-system/fields';

/**
 * Barre de filtres (59 §9.2) : seulement les champs que la vue expose, eux-mêmes filtrables au
 * modèle ; la syntaxe gouvernée de l'API (`filter[champ][op]`) est la seule traduction. Le serveur
 * refuse tout filtre hors de ce cadre (ADR-009).
 */

export type Filters = Record<string, Record<string, string>>;

const OPERATOR_SIGNS: Readonly<Record<string, string>> = { gte: '≥', gt: '>', lte: '≤', lt: '<', ne: '≠', contains: 'contient', starts_with: 'commence par', exists: 'renseigné :' };

export function FilterBar({ fields, object, value, onApply }: { fields: readonly string[]; object: ObjectSummary | undefined; value: Filters; onApply: (filters: Filters) => void }) {
  const [draft, setDraft] = useState<Filters>(value);
  // L'URL fait foi (ADR-094) : un retour arrière ou un « Effacer les filtres » remet le brouillon à jour.
  const serialized = JSON.stringify(value);
  useEffect(() => setDraft(JSON.parse(serialized) as Filters), [serialized]);
  if (fields.length === 0) return null;
  const set = (field: string, operator: string, next: string): void => setDraft({ ...draft, [field]: { ...(draft[field] ?? {}), [operator]: next } });
  const active = Object.entries(value).flatMap(([field, operators]) => Object.entries(operators).filter(([, entry]) => entry !== '').map(([operator, entry]) => ({ field, operator, entry })));

  return (
    <form
      className="ay-filterbar"
      role="search"
      onSubmit={(event) => {
        event.preventDefault();
        onApply(draft);
      }}
    >
      {fields.map((fieldId) => {
        const field = fieldOf(object, fieldId);
        const label = fieldLabel(object, fieldId);
        if (field?.type === 'enumeration' || field?.type === 'boolean') {
          const options = field.type === 'boolean' ? ['true', 'false'] : (field.enum_values ?? []);
          return (
            <label key={fieldId} className="ay-field ay-field--inline">
              <span className="ay-field__label">{label}</span>
              <select value={draft[fieldId]?.['eq'] ?? ''} onChange={(event) => set(fieldId, 'eq', event.target.value)}>
                <option value="">Tous</option>
                {options.map((option) => (
                  <option key={option} value={option}>
                    {field.type === 'boolean' ? (option === 'true' ? 'Oui' : 'Non') : text(field.enum_labels?.[option], 'fr', option)}
                  </option>
                ))}
              </select>
            </label>
          );
        }
        if (field?.type === 'date' || field?.type === 'datetime' || field?.type === 'number' || field?.type === 'decimal') {
          const input = field.type === 'date' || field.type === 'datetime' ? 'date' : 'number';
          return (
            <fieldset key={fieldId} className="ay-field ay-field--range">
              <legend className="ay-field__label">{label}</legend>
              <input type={input} aria-label={`${label} minimum`} value={draft[fieldId]?.['gte'] ?? ''} onChange={(event) => set(fieldId, 'gte', event.target.value)} />
              <input type={input} aria-label={`${label} maximum`} value={draft[fieldId]?.['lte'] ?? ''} onChange={(event) => set(fieldId, 'lte', event.target.value)} />
            </fieldset>
          );
        }
        // Texte : « contient », sans casse, saisie échappée par le serveur (ADR-094) ; une référence reste une égalité.
        const textOperator = field?.type === 'string' ? 'contains' : 'eq';
        return (
          <label key={fieldId} className="ay-field ay-field--inline">
            <span className="ay-field__label">{label}</span>
            <ClearableInput type="text" value={draft[fieldId]?.[textOperator] ?? ''} onValueChange={(next) => set(fieldId, textOperator, next)} clearLabel={`Effacer « ${label} »`} />
          </label>
        );
      })}
      <button type="submit" className="ay-button">
        Filtrer
      </button>
      {active.length > 0 && (
        <button
          type="button"
          className="ay-button ay-button--secondary"
          onClick={() => {
            setDraft({});
            onApply({});
          }}
        >
          Réinitialiser
        </button>
      )}
      {active.length > 0 && (
        <ul className="ay-chips" aria-label="Filtres actifs">
          {active.map((chip) => (
            <li key={`${chip.field}.${chip.operator}`} className="ay-chip">
              {fieldLabel(object, chip.field)} {OPERATOR_SIGNS[chip.operator] ?? '='} {chip.entry}
            </li>
          ))}
        </ul>
      )}
    </form>
  );
}
